/**
 * Pure line builders for the parent-app change-request families (#1715):
 * `contact_data`, `contract`, `group_allocation` and `child_contact`. Each returns the
 * reviewer-facing "Proposed change" as an ordered list of "Label: value" lines;
 * the note (when present) is always the last line. Bodies arrive untyped from
 * `payload.executor.body`, so every read is defensive.
 */

import { isValid, parseISO } from 'date-fns';
import {
  CONTACT_CHANGE_KEYS,
  getContactFieldLabel,
  getContactPrivacySubTypeLabel,
  getContactValueLabel,
  getContractKindLabel,
  isContactPrivacySubType,
  getWeekdayLabel,
  type I18nLike,
} from '@/types/changeRequest';
import { formatDate } from '@/utils/dateUtils';
import type { billing_ContractVersionResponse } from '@/api';

type Body = Record<string, unknown> | undefined;

function line(label: string, value: string): string {
  return `${label}: ${value}`;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function noteLabel(i18n: I18nLike): string {
  return i18n._({ id: 'changeRequest.parent.note', message: 'Note' });
}

function noteLine(body: Body, i18n: I18nLike): string[] {
  const note = asString(body?.note).trim();
  return note ? [line(noteLabel(i18n), note)] : [];
}

/**
 * `YYYY-MM-DD` -> the active locale's short date (`01.10.2026` in German).
 * parseISO reads a date-only value as local midnight, so the day never shifts.
 * An unparseable value is shown as sent.
 */
export function formatRequestDate(value: string, locale?: string): string {
  const parsed = parseISO(value);
  return isValid(parsed) ? formatDate(parsed, locale) : value;
}

function weekdaysLine(raw: unknown, i18n: I18nLike): string[] {
  if (!Array.isArray(raw)) return [];
  const days = raw.filter((d): d is number => typeof d === 'number' && Number.isInteger(d));
  if (days.length === 0) return [];
  return [
    line(
      i18n._({ id: 'changeRequest.parent.weekdays', message: 'Weekdays' }),
      days.map((d) => getWeekdayLabel(i18n, d)).join(', '),
    ),
  ];
}

function fromDateLine(body: Body, i18n: I18nLike): string[] {
  const from = asString(body?.from_date);
  return from
    ? [line(i18n._({ id: 'changeRequest.parent.fromDate', message: 'From' }), formatRequestDate(from))]
    : [];
}

/** "Street StreetNumber, Line 2, Zip City, Country" -- empty parts are dropped. */
export function formatRequestAddress(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return '';
  const a = raw as Record<string, unknown>;
  const streetPart = [asString(a.street), asString(a.streetNumber)].filter(Boolean).join(' ');
  const cityPart = [asString(a.zip), asString(a.city)].filter(Boolean).join(' ');
  return [streetPart, asString(a.info2), cityPart, asString(a.country)].filter(Boolean).join(', ');
}

/**
 * Display value of one `contact_data` change: the address on one line, the
 * nationalities as a comma list, the birthdate as a local date, coded values
 * (form of address, gender, language) as labels. "" means cleared.
 */
function contactChangeValue(key: string, raw: unknown, i18n: I18nLike): string {
  if (key === 'address') return formatRequestAddress(raw);
  if (key === 'nationalities') {
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string').join(', ') : '';
  }
  const value = asString(raw).trim();
  if (!value) return '';
  if (key === 'birthdate') return formatRequestDate(value);
  return getContactValueLabel(i18n, key, value);
}

/**
 * `contact_data`: one line per changed key, in the canonical key order
 * (unknown keys follow in the order sent), then the note. A privacy request
 * (`subType` data_access_request / account_deletion_request, #1876) changes no
 * field: it shows its label and the note, never a "changes" block.
 */
export function contactDataLines(body: Body, i18n: I18nLike, subType?: string): string[] {
  if (isContactPrivacySubType(subType)) {
    return [getContactPrivacySubTypeLabel(i18n, subType), ...noteLine(body, i18n)];
  }
  const changes = body?.changes;
  const lines: string[] = [];
  if (changes && typeof changes === 'object' && !Array.isArray(changes)) {
    const c = changes as Record<string, unknown>;
    const known: readonly string[] = CONTACT_CHANGE_KEYS;
    const keys = [...known.filter((k) => k in c), ...Object.keys(c).filter((k) => !known.includes(k))];
    for (const key of keys) {
      const value = contactChangeValue(key, c[key], i18n);
      // A cleared field is sent as "": the parent removed the value.
      const shown = value || i18n._({ id: 'changeRequest.parent.clearedValue', message: '(cleared)' });
      lines.push(line(getContactFieldLabel(i18n, key), shown));
    }
  }
  return [...lines, ...noteLine(body, i18n)];
}

/**
 * The requested week of a care_days request: one line per weekday with its
 * service codes, named by `serviceLabel` (the code itself when unknown).
 */
function requestedServicesLines(
  raw: unknown,
  i18n: I18nLike,
  serviceLabel: (code: string) => string,
): string[] {
  if (!Array.isArray(raw)) return [];
  const days = raw
    .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
    .map((entry) => ({
      weekday: entry.weekday,
      services: Array.isArray(entry.services)
        ? entry.services.filter((code): code is string => typeof code === 'string' && code !== '')
        : [],
    }))
    .filter((day): day is { weekday: number; services: string[] } =>
      typeof day.weekday === 'number' && Number.isInteger(day.weekday) && day.services.length > 0,
    )
    .sort((a, b) => a.weekday - b.weekday);
  if (days.length === 0) return [];
  return [
    i18n._({ id: 'changeRequest.parent.requestedServices', message: 'Requested week' }),
    ...days.map((day) => `  ${line(getWeekdayLabel(i18n, day.weekday), day.services.map(serviceLabel).join(' + '))}`),
  ];
}

/**
 * `contract`: kind, weekdays, the requested week (services per weekday), days
 * per week, from / to date, then the note.
 */
export function contractLines(
  body: Body,
  i18n: I18nLike,
  serviceLabel: (code: string) => string = (code) => code,
): string[] {
  const lines: string[] = [];
  const kind = asString(body?.kind);
  if (kind) {
    lines.push(
      line(i18n._({ id: 'changeRequest.parent.contractKind', message: 'Requested change' }), getContractKindLabel(i18n, kind)),
    );
  }
  lines.push(...weekdaysLine(body?.requested_weekdays, i18n));
  lines.push(...requestedServicesLines(body?.requested_services, i18n, serviceLabel));
  const perWeek = body?.attendance_days_per_week;
  if (typeof perWeek === 'number') {
    lines.push(line(i18n._({ id: 'changeRequest.parent.daysPerWeek', message: 'Days per week' }), String(perWeek)));
  }
  lines.push(...fromDateLine(body, i18n));
  const to = asString(body?.to_date);
  if (kind === 'care_days') {
    // A care-days change is either permanent or for a limited period only.
    lines.push(
      to
        ? line(
            i18n._({
              id: 'changeRequest.parent.onlyUntil',
              message: 'Only until (then the previous week applies again)',
            }),
            formatRequestDate(to),
          )
        : line(
            i18n._({ id: 'changeRequest.parent.duration', message: 'Duration' }),
            i18n._({ id: 'changeRequest.parent.permanent', message: 'Permanent' }),
          ),
    );
  } else if (to) {
    const toLabel =
      kind === 'terminate'
        ? i18n._({ id: 'changeRequest.parent.lastDayOfCare', message: 'Last day of care' })
        : i18n._({ id: 'changeRequest.parent.toDate', message: 'To' });
    lines.push(line(toLabel, formatRequestDate(to)));
  }
  return [...lines, ...noteLine(body, i18n)];
}

/**
 * `group_allocation`: current group (only when its name was resolved),
 * requested group name, weekdays, from date, then the note.
 */
export function groupAllocationLines(body: Body, i18n: I18nLike, currentGroupName?: string): string[] {
  const lines: string[] = [];
  if (currentGroupName) {
    lines.push(line(i18n._({ id: 'changeRequest.parent.currentGroup', message: 'Current group' }), currentGroupName));
  }
  const requested = asString(body?.requested_group_name).trim();
  if (requested) {
    lines.push(line(i18n._({ id: 'changeRequest.parent.requestedGroup', message: 'Requested group' }), requested));
  }
  lines.push(...weekdaysLine(body?.requested_weekdays, i18n));
  lines.push(...fromDateLine(body, i18n));
  return [...lines, ...noteLine(body, i18n)];
}

/**
 * `child_contact`: the new person (name, relationship, mobile with its country,
 * e-mail), the requested roles, then the note. `relationshipLabel` names the
 * relationship enum (the raw value when unknown).
 */
export function childContactLines(
  body: Body,
  i18n: I18nLike,
  relationshipLabel: (type: string) => string = (type) => type,
): string[] {
  const lines: string[] = [];
  const name = [asString(body?.firstname).trim(), asString(body?.lastname).trim()].filter(Boolean).join(' ');
  if (name) {
    lines.push(line(i18n._({ id: 'changeRequest.parent.contactName', message: 'Name' }), name));
  }
  const relationship = asString(body?.relationship_type).trim();
  if (relationship) {
    lines.push(
      line(
        i18n._({ id: 'changeRequest.parent.relationship', message: 'Relationship to the child' }),
        relationshipLabel(relationship),
      ),
    );
  }
  const mobile = asString(body?.tel_mobile).trim();
  if (mobile) {
    const country = asString(body?.tel_mobile_country_code).trim().toUpperCase();
    lines.push(
      line(
        i18n._({ id: 'changeRequest.parent.mobile', message: 'Mobile' }),
        country ? `${mobile} (${country})` : mobile,
      ),
    );
  }
  const email = asString(body?.email).trim();
  if (email) {
    lines.push(line(i18n._({ id: 'changeRequest.parent.email', message: 'E-mail' }), email));
  }
  const roles: string[] = [];
  if (body?.emergency_contact === true) {
    roles.push(i18n._({ id: 'changeRequest.subType.contact_emergency', message: 'Emergency contact' }));
  }
  if (body?.pickup_authorized === true) {
    roles.push(i18n._({ id: 'changeRequest.subType.contact_pickup', message: 'Pick-up authorization' }));
  }
  if (roles.length > 0) {
    lines.push(line(i18n._({ id: 'changeRequest.parent.contactRoles', message: 'Requested as' }), roles.join(', ')));
  }
  return [...lines, ...noteLine(body, i18n)];
}

export interface ContractGroupSummary {
  /** The version to link to: the one in effect today, else the next upcoming one, else the latest. */
  versionId: string;
  /**
   * Start of the group as a calendar date (YYYY-MM-DD): the earliest start date of a
   * version that was not cancelled.
   */
  startDate: string;
  /** `effective_status` of the linked version (in_effect, upcoming, expired, cancelled, terminated). */
  status: string;
}

/**
 * Identifies the contract group a parent `contract` request refers to, from the
 * group's versions. Returns null when there is no version to show.
 *
 * `start_date` may arrive as RFC3339 (`2026-03-01T00:00:00Z`); only its calendar date is
 * kept, so a later `parseISO` in a timezone west of UTC cannot shift it to the day before
 * (#1716).
 */
export function summarizeContractGroup(
  versions: readonly billing_ContractVersionResponse[] | undefined,
): ContractGroupSummary | null {
  const withId = (versions ?? []).filter((v) => !!v.id);
  if (withId.length === 0) return null;
  const byStart = [...withId].sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? ''));
  const status = (v: billing_ContractVersionResponse) => (v.effective_status ?? '').toLowerCase();
  const linked =
    byStart.find((v) => status(v) === 'in_effect') ??
    byStart.find((v) => status(v) === 'upcoming') ??
    byStart[byStart.length - 1];
  const live = byStart.filter((v) => status(v) !== 'cancelled');
  const first = (live.length > 0 ? live : byStart).find((v) => !!v.start_date);
  return {
    versionId: linked.id as string,
    startDate: first?.start_date ? first.start_date.slice(0, 10) : '',
    status: status(linked),
  };
}

/** Localized label of a contract `effective_status`; an unknown value is shown as sent. */
export function contractStatusLabel(status: string, i18n: I18nLike): string {
  switch (status) {
    case 'in_effect':
      return i18n._({ id: 'common.active', message: 'Active' });
    case 'upcoming':
      return i18n._({ id: 'common.upcoming', message: 'Upcoming' });
    case 'expired':
      return i18n._({ id: 'common.expired', message: 'Expired' });
    case 'cancelled':
      return i18n._({ id: 'common.cancelled', message: 'Cancelled' });
    case 'terminated':
      return i18n._({ id: 'common.terminated', message: 'Terminated' });
    default:
      return status;
  }
}
